import { useEffect, useState } from "react";
import { Button, message } from "antd";
import { FormActions } from "../../components/common/FormActions";
import {
  createColumn,
  formatDateTime,
  createViewAction,
  numberFormat,
} from "../../utils/helpers";
import useDownload from "../common/useDownload";
import { API_DOWNLOAD_RECEIPT } from "../../utils/apis";
import { URL_PAID_REQUESTS } from "../../utils/urls";

// Helper: Format Status
const formatStatus = (status) => {
  const statusMap = {
    A: "Unpaid",
    P: "Pending",
    R: "Rejected",
    C: "Paid",
    F: "Failed",
  };
  return statusMap[status] || status;
};

export const usePaidRequests = () => {
  // Download functionality
  const {
    downloadFile,
    success: downloadSuccess,
    error: downloadError,
  } = useDownload();

  // Track downloading state for each row
  const [downloadingRows, setDownloadingRows] = useState(new Set());

  // The register layout already fetches API_PAID_REQUESTS with the active
  // filters, so this hook must not fetch it again.

  // Handle download success or error
  useEffect(() => {
    if (downloadSuccess) {
      message.success("File downloaded successfully");
      // Clear downloading state for all rows when download completes
      setDownloadingRows(new Set());
    }

    if (Object?.keys(downloadError)?.length > 0) {
      message.error(downloadError?.message || "Failed to download file");
      // Clear downloading state for all rows when download fails
      setDownloadingRows(new Set());
    }
  }, [downloadSuccess, downloadError]);

  const tableColumns = () => [
    createColumn("invoice_number", true),
    createColumn("transaction_id", true),
    createColumn("amount", false),
    createColumn("charge", false),
    createColumn("payment_mode_name", true, "Payment Mode"),
    createColumn("phone_number", true),
    createColumn("payment_date", true, "Payment Date"),
    // createColumn("payment_date", {
    //   type: "date",
    //   dateRangeNames: {
    //     startDateKey: "payment_date_min_date",
    //     endDateKey: "payment_date_max_date",
    //   },
    // }),
    createColumn("receipt", false, "Receipt"),
    createColumn("actions", false),
  ];

  const handleDownloadReceipt = (prn, rowId) => {
    // Check if this specific row is already downloading
    if (downloadingRows.has(rowId)) {
      return;
    }

    // Add this row to downloading state
    setDownloadingRows((prev) => new Set(prev).add(rowId));

    // Generate filename
    const fileName = `Receipt_${prn}_${Date.now()}.pdf`;

    // Use the useDownload hook
    downloadFile({
      api: API_DOWNLOAD_RECEIPT,
      params: {
        id: prn,
      },
      filename: fileName,
    });
  };

  const tableData = (data) => {
    if (!data) return [];

    return data.map((item) => {
      const prn = item.prn || {};
      const invoiceDetails = prn.invoice_details || {};
      const isDownloading = downloadingRows.has(item?.id);
      const hasPRN = item?.id;
      //prn?.ref && prn?.ref.trim() !== "";

      return {
        ...item,
        transaction_id: prn?.transaction_id,
        invoice_number: invoiceDetails.invoice_number || "N/A",
        amount: numberFormat(prn?.amount),
        charge: item?.charge,
        payment_mode_name: prn?.payment_mode_name || "N/A",
        phone_number: prn?.phone_number || "N/A",
        // status: formatStatus(prn.status),
        // payment_date: formatDateTime(item.payment_date),
        payment_date: item?.payment_date,
        receipt: hasPRN ? (
          <Button
            onClick={() => handleDownloadReceipt(item?.id, item?.id)}
            className="font12 noShadow"
            type="primary"
            loading={isDownloading}
            disabled={isDownloading}
          >
            {isDownloading ? "Downloading..." : "Download Receipt"}
          </Button>
        ) : null, // Don't render button if PRN is empty
        actions: (
          <FormActions
            actions={[createViewAction(URL_PAID_REQUESTS, item.id)]}
          />
        ),
      };
    });
  };

  const viewData = (item) => {
    if (!item) {
      return {
        sections: [
          {
            title: "Error",
            fields: { message: "No data available" },
          },
        ],
      };
    }

    const prn = item.prn || {};
    const invoice = prn.invoice_details || {};

    return {
      sections: [
        {
          title: "Paid Request Information",
          fields: {
            amount: prn.amount || "N/A",
            status: formatStatus(prn.status),
            payment_mode: prn.payment_mode_name || "N/A",
            phone_number: prn.phone_number || "N/A",
            transaction_id: prn.transaction_id || "N/A",
            payment_date: item?.payment_date,
            declaration: prn.declaration ? "Accepted" : "Not accepted",
            deleted: prn.deleted ? "Yes" : "No",
          },
        },
        {
          title: "Invoice Details",
          fields: {
            invoice_number: invoice.invoice_number || "N/A",
            invoice_amount: invoice.amount || "N/A",
            invoice_type: invoice.type || "N/A",
            exempted: invoice.exempted ? "Yes" : "No",
            object_id: invoice.object_id || "N/A",
          },
        },
        {
          title: "System Information",
          fields: {
            record_id: item.id,
            created_at: formatDateTime(item.created_at),
            updated_at: formatDateTime(item.updated_at),
            created_by: item.created_by
              ? `${item.created_by.first_name} ${item.created_by.last_name}`
              : "System",
            updated_by: item.updated_by
              ? `${item.updated_by.first_name} ${item.updated_by.last_name}`
              : "Not updated",
            migrated: item.migrated ? "Yes" : "No",
          },
        },
      ],
    };
  };

  const activityFilter = "payment_requests";

  return {
    tableData,
    tableColumns,
    viewData,
    activityFilter,
  };
};
